import { CascadiaColor } from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";
import { mergeProps } from "react-aria";
import {
  Row as AriaRow,
  Button,
  Collection,
  RowProps,
  useTableOptions,
} from "react-aria-components";

import { Cell } from "./Cell";
import { TableCheckbox } from "./TableCheckbox";

interface ExtendedRowProps<T> extends RowProps<T> {
  backgroundColor?: CascadiaColor;
  hasStretchedRowLink?: boolean;
}

export function Row<T extends object>({
  id,
  columns,
  children,
  backgroundColor,
  hasStretchedRowLink,
  ...otherProps
}: ExtendedRowProps<T>) {
  const { selectionBehavior, allowsDragging } = useTableOptions();

  return (
    <AriaRow
      id={id}
      data-stretched-row={hasStretchedRowLink ? "true" : undefined}
      {...mergeProps(otherProps, {
        className: cx(
          css({
            ...styles.row,
            backgroundColor: backgroundColor && `var(--${backgroundColor})`,
          }),
          hasStretchedRowLink && styles.stretchedRow
        ),
      })}
    >
      {allowsDragging && (
        <Cell>
          <Button slot="drag">≡</Button>
        </Cell>
      )}
      {selectionBehavior === "toggle" && (
        <Cell
          className={cx(
            styles.checkboxCell,
            hasStretchedRowLink && styles.stretchedCheckboxCell
          )}
        >
          <TableCheckbox />
        </Cell>
      )}
      <Collection items={columns}>{children}</Collection>
    </AriaRow>
  );
}

const styles = {
  row: {
    outline: "none",
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
  },
  stretchedRow: css({
    position: "relative",
    "& button, & [role=checkbox], & [slot=selection], & .react-aria-Checkbox, & td[data-action]":
      {
        position: "relative",
        zIndex: 2,
      },
  }),
  checkboxCell: css({
    paddingLeft: "var(--space-2)",
    paddingRight: "var(--space-1)",
  }),
  stretchedCheckboxCell: css({
    position: "relative",
    zIndex: 2,
  }),
};
